/**
 * reecritureDeParagraphes — les décisions PURES de `rewrite_block` : quels
 * paragraphes la liste désigne, quelles étapes partent à l'éditeur, et ce que
 * le reçu en dit. L'exécuteur (`rewriteBlockExecutor.ts`) parle à l'éditeur ;
 * ce module ne le connaît que par ce qu'on lui passe, et se teste sans lui.
 *
 * Les faits mesurés dont ces décisions dépendent sont au §17 de
 * `docs/features/redaction-web-canvas/superdoc-document-api.md`.
 */

import {
  CANVAS_FORMAT_REMOVE,
  CANVAS_REWRITE_BLOCKS_SKIPPED,
  CANVAS_REWRITE_FORMATTING_NOT_CARRIED,
  CANVAS_TEXT_FORMAT_SETTING_KEYS,
  type CanvasActionInput,
  type CanvasFormatSettingOutcome,
  type CanvasFormatPlace,
  type CanvasFormatSettingValue,
  type CanvasParagraphContentKind,
  type CanvasRewriteReceipt,
  type CanvasTextFormatSettingKey,
  plierLaTypographie,
} from '@hector/shared-types';

import type { CanvasActionReceipt, VerificationCheck } from './actionReceipt';
import { estUnTexteDeRevisionCoupe, texteCommeUneRevisionLePublie, type CanvasSnapshotRevision } from './canvasSnapshot';
import { resoudreLaCibleDeParagraphes, type ParagrapheCible } from './cibleDeParagraphes';
import type { ClotureDeLEcriture, Refus } from './ecritureDeForme';
import { direLeLieu } from './horsDuCorps';
import type { ActeVise, FormeDeParagraphe, FormeDePassage, ParagrapheVise } from '@hector/docx-forme';
import { lireLesMarques, NATURES_DE_MARQUE, type NatureDeMarque, type TexteMarque } from './marquesDeTexte';
import { texteDuParagraphe, type EtapeAEcrire, type EtapeDePlan } from './planDeFormatText';
import { annulationDeLEcriture, PAGE_NON_REPEINTE } from './recuDEcritureDeForme';
import { ecritureDuReglage, jugerLeReglage, normaliserLaDemande, passageLuPour } from './reglagesDeTexte';
import type { ReglagesDeTexte } from './verificationDeForme';
import { messageTargetNotInFinalInspection } from './selectorResolver';

type Reecriture = Extract<CanvasActionInput, { action: 'rewrite_block' }>;

const INTENTION = 'canvas_perform_action rewrite_block';

/** Un paragraphe de la liste, résolu contre le fichier de l'acte. */
export interface ParagrapheAReecrire {
  nodeId: string;
  /** Le paragraphe tel que le fichier le porte avant l'écriture, rendu par la résolution commune des cibles. */
  avant: ParagrapheCible;
  /** Le texte à écrire, sans les délimiteurs de ses marques. */
  texte: string;
  /** Les plages du texte que ses marques `**`, `*`, `__` couvrent. */
  marques: TexteMarque['marques'];
}

/** Des identifiants cités dans un message : tous — la liste d'un appel est bornée (`CANVAS_REWRITE_BLOCKS_MAX`), aucun n'est compté à part. */
const citer = (ids: readonly string[]): string => ids.join(', ');

/**
 * Résout la liste contre le fichier de l'acte, ou refuse avant toute écriture
 * en nommant les identifiants en cause : la liste est réécrite entière ou pas
 * du tout tant que l'erreur est dans l'appel.
 *
 * Seul le corps est visé, cellules de tableau exclues : ce que l'éditeur fait
 * d'une réécriture dans une cellule n'est pas mesuré.
 *
 * Un identifiant que le corps ne porte pas a trois causes, et trois refus : un
 * paragraphe d'en-tête, de pied ou de note (un autre verbe l'écrit) ; un bloc
 * que la lecture a publié et qui n'est pas un paragraphe du fichier — tableau,
 * zone de texte — (aucun verbe ne le réécrit, relire n'y change rien) ; un
 * identifiant que rien ne connaît (à relire).
 *
 * @param inspectes Les identifiants que la dernière lecture finale a publiés ; `undefined` : l'enveloppe n'en porte pas.
 * @param lieuHorsDuCorpsDe Où le fichier porte un paragraphe hors du corps (`EcritureDeFormeOuverte.lieuHorsDuCorpsDe`).
 */
export function viserLesParagraphes(
  acte: ActeVise,
  blocks: Reecriture['blocks'],
  inspectes: ReadonlySet<string> | undefined,
  lieuHorsDuCorpsDe: (nodeId: string) => CanvasFormatPlace | undefined = () => undefined,
): { paragraphes: ParagrapheAReecrire[] } | { refus: Refus } {
  // UNE résolution d'identifiants pour tous les verbes du socle : une liste nomme chaque paragraphe, cellules comprises.
  const cible = resoudreLaCibleDeParagraphes(acte, 'cellules_par_identifiants', { kind: 'ids', nodeIds: blocks.map((block) => block.nodeId) });
  const parIdentifiant = new Map(cible.paragraphes.map((lu) => [lu.nodeId, lu] as const));
  const horsDuCorps = cible.nonResolus.flatMap(({ nodeId }) => {
    const lieu = lieuHorsDuCorpsDe(nodeId);
    return lieu === undefined ? [] : [{ nodeId, lieu }];
  });
  if (horsDuCorps.length > 0) {
    const notes = horsDuCorps.some(({ lieu }) => lieu.kind === 'footnote');
    const zones = horsDuCorps.some(({ lieu }) => lieu.kind !== 'footnote');
    return {
      refus: {
        code: 'REWRITE_TARGET_OUTSIDE_BODY',
        reessayer: true,
        message: `${INTENTION} was refused before any write: ${horsDuCorps.length} of the ${blocks.length} nodeId(s) designate a paragraph outside the body, which this verb does not rewrite — `
          + `${horsDuCorps.map(({ nodeId, lieu }) => `${nodeId} (${direLeLieu(lieu)})`).join(', ')}. Nothing was written. `
          + `${zones ? 'A header or a footer is written with set_header_footer. ' : ''}${notes ? 'A footnote is written with edit_footnote. ' : ''}`
          + 'Resend the list without these ids.',
      },
    };
  }
  // Le fichier porte cet identifiant sur DEUX paragraphes : aucun ne s'adresse, rien ne dit lequel l'éditeur écrirait.
  const enDouble = cible.nonResolus.filter(({ reason }) => reason === 'duplicate_node_id').map(({ nodeId }) => nodeId);
  if (enDouble.length > 0) {
    return {
      refus: {
        code: 'REWRITE_TARGET_DUPLICATED',
        reessayer: true,
        message: `${INTENTION} was refused before any write: the document file carries ${citer(enDouble)} on more than one paragraph, so this id addresses none of them. `
          + 'Nothing was written, and reading again will not change it. Resend the list without these ids; change their text by passage (replace_text with a scope of unique text), or tell the lawyer.',
      },
    };
  }
  const introuvables = cible.nonResolus.map(({ nodeId }) => nodeId);
  // La dernière lecture les a publiés : ils existent dans l'éditeur, et ne sont pas des paragraphes du fichier.
  const publies = introuvables.filter((nodeId) => inspectes?.has(nodeId) === true);
  if (publies.length > 0) {
    return {
      refus: {
        code: 'REWRITE_TARGET_NOT_A_PARAGRAPH',
        reessayer: true,
        message: `${INTENTION} was refused before any write: ${citer(publies)} ${publies.length === 1 ? 'is a block' : 'are blocks'} of this document, but not a paragraph of its body — `
          + 'a table (tbl:…), or a paragraph inside a text box or a shape. This verb rewrites none of them. Nothing was written, and reading again will not change it. Resend the list without these ids. '
          + 'The text of a TABLE is changed cell by cell, by passage: replace_text with edits:[{find,replace}] and scope:{kind:"nodeId",nodeId:"<the nodeId of the cell paragraph, as your reading published it, or this tbl:… id>"}. '
          + 'No verb is verified to write inside a text box or a shape: tell the lawyer the text to change there.',
      },
    };
  }
  if (introuvables.length > 0) {
    return {
      refus: {
        code: 'REWRITE_TARGET_NOT_FOUND',
        message: `${INTENTION} was refused before any write: ${introuvables.length} of the ${blocks.length} nodeId(s) designate no paragraph of this document `
          + `(${citer(introuvables)}). A nodeId is the id of a reading marker of THIS document, copied as is. Nothing was written. `
          + 'Read the document again (canvas_inspect) and resend the whole list with ids it publishes.',
      },
    };
  }
  const enCellule = cible.cellules.rendues;
  if (enCellule.length > 0) {
    // Les deux voies nommées sont celles que `tests/ws-mocked/canvas-recus-vrais-cellule-de-tableau.spec.ts` tient sur
    // l'éditeur réel (2026-10-13), avec leurs arguments : le même nodeId, un passage du texte de la cellule.
    return {
      refus: {
        code: 'REWRITE_TABLE_CELL_UNSUPPORTED',
        reessayer: true,
        message: `${INTENTION} was refused before any write: ${citer(enCellule)} ${enCellule.length === 1 ? 'is a table cell paragraph' : 'are table cell paragraphs'}, `
          + 'and this verb does not rewrite a table cell. Nothing was written. Resend the list without these ids, and change each cell by passage, on the same nodeId: '
          + 'replace_text with edits:[{find,replace}] and scope:{kind:"nodeId",nodeId:"<cell paragraph id>"}, one call per cell; '
          + 'or one apply_edit_plan with a step {id, op:"text_rewrite", find, replace, selector:{kind:"nodeId",nodeId:"<cell paragraph id>"}} per cell. '
          + 'find is a passage of the cell\'s current text (its whole text to replace it all), searched in that cell only.',
      },
    };
  }
  const horsDePortee = inspectes === undefined ? [] : blocks.filter((block) => !inspectes.has(block.nodeId)).map((block) => block.nodeId);
  if (horsDePortee.length > 0) {
    return { refus: { code: 'TARGET_NOT_IN_FINAL_INSPECTION', message: `${messageTargetNotInFinalInspection()} Targets not published: ${citer(horsDePortee)}.` } };
  }
  const paragraphes = blocks.map((block) => ({ nodeId: block.nodeId, avant: parIdentifiant.get(block.nodeId)!, ...lireLesMarques(block.text) }));
  // Un paragraphe SANS texte est écrit comme un autre : l'étape adressée par bloc y pose le texte, sa forme de paragraphe gardée (§17.1).
  return { paragraphes };
}

/** Comment le refus nomme au modèle ce qu'un paragraphe porte hors de son texte. */
const NOM_DE_LELEMENT: Readonly<Record<CanvasParagraphContentKind, string>> = {
  footnote_reference: 'a footnote call',
  endnote_reference: 'an endnote call',
  field: 'a field (date, page number or cross-reference)',
  hyperlink: 'a link',
  bookmark: 'a bookmark around text',
  line_break: 'a manual line break',
  tab: 'a tab',
  image_in_text: 'an image inside the text',
  pending_tracked_change: 'a pending tracked change',
  comment_anchor: 'a comment anchor',
};

/**
 * Ce qu'une réécriture du paragraphe ENTIER détruit, vide ou fait refuser par l'éditeur — relevé du 2026-10-09, §17.6
 * de `superdoc-document-api.md`. Deux éléments n'y sont pas : la tabulation, que le texte neuf peut porter (`\t`), et
 * l'ancre de commentaire, qui part en fin de paragraphe sans que le commentaire soit perdu (dite au reçu).
 */
const DETRUITS_PAR_LA_REECRITURE: ReadonlySet<CanvasParagraphContentKind> = new Set([
  'footnote_reference', 'endnote_reference', 'field', 'hyperlink', 'bookmark', 'line_break', 'image_in_text', 'pending_tracked_change',
]);

const tabulationsDe = (texte: string): number => texte.split('\t').length - 1;

/**
 * Pourquoi ce paragraphe ne peut pas être réécrit en entier, avec son issue ; `undefined` : rien ne l'empêche.
 *
 * WHY refuser, et pour ce seul paragraphe : réécrire un paragraphe ne détruit rien en silence. L'étape `text.rewrite`
 * remplace tout son contenu par du texte : l'appel de note part avec sa note, le champ et le lien sont vidés, le saut de
 * ligne disparaît, la révision en attente d'un confrère aussi — sous un reçu `ok`. Les verbes par PASSAGE gardent ces
 * éléments (mesuré, même section) : le refus les nomme. Les autres paragraphes de la liste sont écrits.
 *
 * ⚠️ La révision en attente a son issue à elle : sa décision. Le refus y promettait les verbes par passage, qui ont
 * échoué à leur tour sur ces paragraphes (conversation de production du 2026-10-13). Tant qu'elle est ouverte, aucun
 * verbe n'est promis sur le paragraphe, même pour ses autres éléments.
 *
 * Un texte déjà en place n'est pas réécrit : rien ne l'empêche.
 *
 * @param revisionReprise Vrai quand la seule révision en attente du paragraphe est celle qu'une réécriture reprend
 *   sans la détruire ({@link remplacementEntierEnAttente}) : elle n'empêche alors rien.
 */
export function empechementDeReecriture(paragraphe: ParagrapheAReecrire, revisionReprise = false): string | undefined {
  if (texteDejaEnPlace(paragraphe)) return undefined;
  const { horsTexte } = paragraphe.avant;
  const detruits = horsTexte.filter((element) => DETRUITS_PAR_LA_REECRITURE.has(element) && !(revisionReprise && element === 'pending_tracked_change'));
  const [avant, demandees] = [tabulationsDe(texteDuParagraphe(paragraphe.avant)), tabulationsDe(paragraphe.texte)];
  const tabulationsPerdues = horsTexte.includes('tab') && demandees < avant;
  if (detruits.length === 0 && !tabulationsPerdues) return undefined;
  if (detruits.length === 0) {
    return `not rewritten: its text has ${avant} tab(s) and the new text ${demandees}, so a rewrite would delete ${avant - demandees}. `
      + 'Resend this paragraph with a tab character (\\t) where each tab belongs, or change its passages (replace_text), which keeps them.';
  }
  const noms = [...detruits, ...(tabulationsPerdues ? ['tab' as const] : [])].map((element) => NOM_DE_LELEMENT[element]).join(', ');
  // Mesuré : derrière un appel de note, l'éditeur refuse aussi l'écriture d'un passage (`REVISION_MISMATCH`, `ADDRESS_STALE`).
  const apresUneNote = detruits.some((element) => element === 'footnote_reference' || element === 'endnote_reference')
    ? ' The editor refuses to change a passage located AFTER a note call: tell the lawyer what stays to be changed there.'
    : '';
  const parPassage = `(replace_text, or text_rewrite steps of apply_edit_plan): they keep it.${apresUneNote}`;
  if (!detruits.includes('pending_tracked_change')) {
    return `not rewritten: it carries ${noms}, which a whole-paragraph rewrite would delete. Change its text passage by passage instead ${parPassage}`;
  }
  const aDecider = `not rewritten: it carries ${noms}, which a whole-paragraph rewrite would delete. The pending tracked change has to be decided before this paragraph is rewritten: `
    + 'the lawyer accepts or rejects it in the document, or, with their agreement, call accept_tracked_changes with target:{kind:"ids",ids:[…]} — '
    + 'the ids are the trackedChanges[].id whose blockId is this paragraph, read by canvas_inspect with includeDomains:["blocks","trackedChanges"]. Then resend this paragraph.';
  return detruits.length === 1 && !tabulationsPerdues
    ? aDecider
    : `${aDecider} Once it is decided, a whole-paragraph rewrite would still delete the rest: change its text passage by passage ${parPassage}`;
}

/**
 * La révision `replacement` qu'une réécriture du paragraphe REPREND au lieu de la détruire ; `undefined` :
 * le paragraphe n'est pas dans ce cas, et sa révision en attente garde le refus.
 *
 * WHY : réécrire deux fois le même paragraphe sous suivi était refusé, la première réécriture ayant laissé une
 * révision. Mesuré le 2026-10-13 sur l'éditeur réel (`tests/ws-mocked/mesure-recus-vrais.spec.ts`, D1) : sur ce
 * paragraphe, l'étape `text.rewrite` par bloc garde la MÊME révision, y met le texte neuf en insertion et laisse le
 * texte d'origine en suppression ; « tout rejeter » rend le texte d'origine exact.
 *
 * La limite est celle de la mesure, et stricte. Le fichier dit qu'une révision est là sans dire laquelle : sa nature
 * vient de l'éditeur (`revisions`, liste COMPLÈTE des révisions ouvertes, lue juste avant d'écrire). Il faut :
 * - une seule révision ancrée sur le paragraphe, `replacement` / `text-replacement`, ouverte ;
 * - dont le texte inséré est TOUT le texte du paragraphe lu au fichier : une `replacement` de quelques mots, ou un
 *   paragraphe qui porte aussi une insertion, une suppression ou un déplacement, n'est pas mesuré ; un texte inséré
 *   coupé à la borne du lecteur ne prouve pas qu'il porte tout le texte ;
 * - aucune révision de la liste sans bloc d'ancrage : rien ne dirait qu'elle n'est pas dans ce paragraphe.
 *
 * ⚠️ Rien ne dit QUI a posé cette révision (l'éditeur signe les écritures de l'agent du nom de l'avocat connecté) :
 * la `replacement` d'un confrère qui remplace tout le paragraphe passe la même garde. Son texte proposé est alors
 * remplacé dans sa révision ; le texte d'origine reste celui que le rejet rend.
 */
export function remplacementEntierEnAttente(paragraphe: ParagrapheAReecrire, revisions: readonly CanvasSnapshotRevision[]): CanvasSnapshotRevision | undefined {
  if (revisions.some((revision) => revision.blockId === undefined)) return undefined;
  const ancrees = revisions.filter((revision) => revision.blockId === paragraphe.nodeId);
  const [seule] = ancrees;
  if (ancrees.length !== 1 || seule === undefined || !estUnRemplacementEntierOuvert(seule, paragraphe.nodeId)) return undefined;
  return seule.insertedText === texteCommeUneRevisionLePublie(texteDuParagraphe(paragraphe.avant)) ? seule : undefined;
}

/** Une `replacement` de texte ouverte, ancrée sur `nodeId`, dont le texte inséré est lu en entier. */
function estUnRemplacementEntierOuvert(revision: CanvasSnapshotRevision, nodeId: string): boolean {
  return revision.type === 'replacement' && revision.subtype === 'text-replacement' && revision.state === 'open' && revision.blockId === nodeId
    && revision.insertedText !== undefined && !estUnTexteDeRevisionCoupe(revision.insertedText);
}

/**
 * Vrai quand la réécriture de `nodeId` a PROUVÉ vivre dans la révision reprise : la même preuve que `replace_text`
 * (`revisionsOuvertesAbsorbantes`, `textActionExecutors.ts`). Ouverte avant et après, son texte inséré a changé et
 * vaut, lu en entier, le texte écrit. Qu'elle n'ait pas disparu ne suffit pas : l'éditeur peut l'avoir laissée telle
 * quelle et écrit en direct à côté.
 */
export function repriseProuvee(avant: CanvasSnapshotRevision, apres: readonly CanvasSnapshotRevision[], nodeId: string, texteEcrit: string): boolean {
  const relue = apres.find((revision) => revision.id === avant.id);
  return relue !== undefined && estUnRemplacementEntierOuvert(avant, nodeId) && estUnRemplacementEntierOuvert(relue, nodeId)
    && relue.insertedText !== avant.insertedText && relue.insertedText === texteCommeUneRevisionLePublie(texteEcrit);
}

/** L'identifiant de l'étape de texte du paragraphe de rang `rang` dans la liste. */
const idDeTexte = (rang: number): string => `t${rang}`;

/**
 * Une étape `text.rewrite` par paragraphe, adressée par BLOC : tout son texte,
 * sans le citer — un texte que l'avocat a changé depuis la lecture ne fait pas
 * refuser l'écriture pour un caractère (le verrou est la révision du document).
 * `nodeType` est exigé par la forme de l'étape et ne décide pas du bloc visé :
 * un titre s'adresse de même (§17.1).
 */
export function etapesDeTexte(paragraphes: readonly ParagrapheAReecrire[]): EtapeDePlan[] {
  return paragraphes.map(({ nodeId, texte }, rang) => ({
    id: idDeTexte(rang),
    op: 'text.rewrite',
    where: { by: 'block', nodeType: 'paragraph', nodeId },
    args: { replacement: { text: texte } },
  }));
}

/** Une plage du texte écrit, `fin` exclue. */
interface Plage { debut: number; fin: number }

/** Une forme qu'une partie du texte d'avant portait et que le texte neuf ne porte pas. */
export interface FormeNonReportee {
  /** Le réglage, nommé comme `format_text` le nomme. */
  setting: CanvasTextFormatSettingKey;
  /** Sa valeur sur ce texte, lue au fichier. */
  valeur: string;
  /** Le texte qui la portait : tel que le texte neuf le porte quand il y est encore, tel que le fichier le portait sinon. */
  texte: string;
}

/** Une forme de caractère hors des sept réglages qu'une partie du texte d'avant portait, et ce que la réécriture en a fait. */
export interface FormeHorsReglagesNonReportee {
  /** L'élément du fichier (`smallCaps`, `strike`, `rStyle:…`). */
  element: string;
  /** Le texte qui la portait. */
  texte: string;
  /**
   * `etendue` : elle ouvrait le paragraphe, l'éditeur l'a donnée à TOUT le texte écrit. `perdue` : posée ailleurs, sur
   * un texte que le texte neuf porte encore sans elle. `abandonnee` : posée ailleurs, sur un texte que le texte neuf
   * ne porte plus.
   */
  sort: 'etendue' | 'perdue' | 'abandonnee';
}

/** Ponctuation et blancs de bord d'un passage du fichier : ils appartiennent souvent à la phrase, pas à ce que la forme met en valeur. */
const BORDS_DE_PORTEUR = /^[\s.,;:!?…()«»“”"'–—-]+|[\s.,;:!?…()«»“”"'–—-]+$/g;

/** Part du porteur que le texte neuf doit encore porter d'un seul tenant pour que la forme compte comme perdue. */
const PART_DU_PORTEUR_GARDEE = 0.5;

/** Typographie et casse pliées, caractère pour caractère : les positions du texte plié sont celles du texte d'origine. */
function plierPourComparer(texte: string): string {
  let plie = '';
  for (const caractere of plierLaTypographie(texte)) {
    const minuscule = caractere.toLowerCase();
    plie += minuscule.length === caractere.length ? minuscule : caractere;
  }
  return plie;
}

/** Un mot d'un texte plié, sans sa ponctuation de bord, et sa place dans ce texte. */
interface Mot { mot: string; debut: number; fin: number }

function motsDe(plie: string): Mot[] {
  const mots: Mot[] = [];
  for (const trouve of plie.matchAll(/\S+/g)) {
    const mot = trouve[0].replace(BORDS_DE_PORTEUR, '');
    if (mot.length === 0) continue;
    const debut = trouve.index + trouve[0].indexOf(mot);
    mots.push({ mot, debut, fin: debut + mot.length });
  }
  return mots;
}

/**
 * La plus longue suite de MOTS entiers que `porteur` et `neuf` partagent : son nombre de mots, sa longueur dans le porteur, sa place dans le texte neuf.
 *
 * WHY des mots et pas des caractères : « 12 400 euros » et « 8 300 euros » partagent « 00 euros », qui n'est le
 * passage de personne. Mot à mot, ils ne partagent que « euros ».
 */
function plusLongueSuiteDeMots(porteur: Mot[], neuf: Mot[]): { mots: number; dansLePorteur: number; debut: number; fin: number } | undefined {
  let meilleure: { mots: number; dansLePorteur: number; debut: number; fin: number } | undefined;
  let precedente = new Array<number>(neuf.length + 1).fill(0);
  for (let i = 1; i <= porteur.length; i += 1) {
    const courante = new Array<number>(neuf.length + 1).fill(0);
    for (let j = 1; j <= neuf.length; j += 1) {
      if (porteur[i - 1]!.mot !== neuf[j - 1]!.mot) continue;
      const mots = precedente[j - 1]! + 1;
      courante[j] = mots;
      const dansLePorteur = porteur[i - 1]!.fin - porteur[i - mots]!.debut;
      if (meilleure === undefined || dansLePorteur > meilleure.dansLePorteur) meilleure = { mots, dansLePorteur, debut: neuf[j - mots]!.debut, fin: neuf[j - 1]!.fin };
    }
    precedente = courante;
  }
  return meilleure;
}

const LETTRE_OU_CHIFFRE = /[\p{L}\p{N}]/u;

/**
 * La place de `candidat` dans `texte`, là où il n'est pas un morceau d'un mot plus long ; -1 : nulle part.
 *
 * WHY : « URGENT » n'est pas gardé dans « urgente », ni « de » dans « demande ». Reposer sa forme là mettrait en
 * gras le début d'un autre mot.
 */
function placeEnMotsEntiers(texte: string, candidat: string): number {
  const colle = (voisin: string | undefined, bord: string): boolean => voisin !== undefined && LETTRE_OU_CHIFFRE.test(voisin) && LETTRE_OU_CHIFFRE.test(bord);
  for (let place = texte.indexOf(candidat); place >= 0; place = texte.indexOf(candidat, place + 1)) {
    if (!colle(texte[place - 1], candidat[0]!) && !colle(texte[place + candidat.length], candidat[candidat.length - 1]!)) return place;
  }
  return -1;
}

/**
 * Ce que `texteNeuf` porte encore du texte `porteur` ; `undefined` : il n'en porte plus rien de reconnaissable.
 *
 * WHY cette question : une forme posée sur un montant que l'agent a retiré du paragraphe n'est pas une perte. Le reçu
 * la disait pourtant non reportée, et l'agent enchaînait des `format_text` sur un passage qui n'existait plus
 * (conversation de production : « 14.708,75 euros TTC » en gras, paragraphe réécrit sans ce montant).
 *
 * WHY pas une recherche à l'identique : elle classait « parti » un passage gardé à un détail près — sa ponctuation
 * finale (« …TTC. » devenu « …TTC, »), sa casse (« URGENT » devenu « Urgent »), un mot en moins (« 14.708,75 euros »
 * sans « TTC ») — et le reçu `ok` taisait alors une vraie perte. La comparaison se fait donc en texte affiché
 * (`texteNeuf` est déjà sans les délimiteurs de ses marques), typographie et casse pliées, sans la ponctuation ni les
 * blancs de bord du porteur, en mots entiers ; à défaut du porteur entier, la plus longue suite d'au moins deux mots
 * qu'il partage avec le texte neuf compte dès qu'elle en couvre la moitié.
 *
 * Le pliage garde les positions : le texte rendu est la tranche EXACTE du texte neuf, celle que `format_text`
 * retrouve par `{kind:"text", nodeId, text}`.
 *
 * Un porteur fait de seuls blancs ou de seule ponctuation est cherché tel quel ; un porteur vide n'est porté par rien.
 */
function porteurDansLeTexteNeuf(porteur: string, texteNeuf: string): string | undefined {
  const plie = plierPourComparer(porteur);
  if (plie.length === 0) return undefined;
  const neuf = plierPourComparer(texteNeuf);
  // Du plus fidèle au plus court : avec sa ponctuation (« Objet : » se repose deux-points compris), puis sans elle.
  const cherche = plie.replace(BORDS_DE_PORTEUR, '') || plie;
  for (const candidat of [plie.trim() || plie, cherche]) {
    const entier = placeEnMotsEntiers(neuf, candidat);
    if (entier >= 0) return texteNeuf.slice(entier, entier + candidat.length);
  }
  // Un seul mot partagé ne fait pas un passage gardé, même s'il pèse la moitié d'un porteur court : « 500 euros »
  // devenu « 800 euros » ne garde que « euros », « Monsieur Dupont » devenu « Monsieur Martin » que « Monsieur ».
  const suite = plusLongueSuiteDeMots(motsDe(cherche), motsDe(neuf));
  if (suite === undefined || suite.mots < 2 || suite.dansLePorteur < cherche.length * PART_DU_PORTEUR_GARDEE) return undefined;
  return texteNeuf.slice(suite.debut, suite.fin);
}

/** La forme de caractère d'un texte réécrit : ce qui part à l'éditeur, ce que la relecture doit trouver, et ce qui n'est pas reporté. */
export interface FormeDuTexte {
  /** Les écritures de forme, par plage du texte écrit, dans l'ordre ; vide : la forme que l'éditeur donne est déjà la bonne. */
  ecritures: Array<{ plage: Plage; inline: Record<string, boolean | string | number | null> }>;
  /** Ce que chaque plage doit montrer à la relecture, réglage par réglage ; vide : rien n'était à poser ni à garder. */
  attendus: Array<{ plage: Plage; demandes: ReglagesDeTexte }>;
  /**
   * Les formes posées sur une PARTIE du texte d'avant, que le texte neuf ne porte pas alors qu'il porte encore le texte
   * qui les portait ({@link porteurDansLeTexteNeuf}), dans l'ordre des réglages puis du texte : les seules pertes
   * établies ({@link estUnePerteANommer}), que `format_text` repose.
   */
  nonReportees: FormeNonReportee[];
  /**
   * Les mêmes formes quand le texte neuf ne porte plus le texte qui les portait : parties avec lui, elles ne sont pas
   * une perte. Le reçu les cite pour information, sans erreur.
   */
  abandonnees: FormeNonReportee[];
  /**
   * La forme de caractère HORS des sept réglages (petites capitales, barré, exposant, style de caractère…) qu'une
   * partie du texte d'avant portait. L'éditeur donne au texte écrit la forme du premier passage, elle comprise
   * (§17.7) : posée en tête, elle est `etendue` à tout le texte, que son texte y soit encore ou non ; posée ailleurs,
   * elle est `perdue` ou `abandonnee`, selon que le texte neuf porte encore son texte. Aucun verbe de forme ne la
   * repose : le reçu nomme les deux premières pour que l'avocat le sache. Absent : aucune.
   */
  horsReglages?: FormeHorsReglagesNonReportee[];
}

const estUneMarque = (setting: CanvasTextFormatSettingKey): setting is NatureDeMarque => (NATURES_DE_MARQUE as readonly string[]).includes(setting);

/** Ce que {@link valeurMontree} rend d'un réglage que le lecteur ne résout pas (couleur de thème, style introuvable…). */
const NON_LISIBLE = 'unreadable';

/** Ce qu'un passage montre d'un réglage, sous une forme qui se compare. */
const valeurMontree = (passage: FormeDePassage, setting: CanvasTextFormatSettingKey): string => {
  const lu = passage[setting];
  return lu.lisible ? String(lu.valeur) : NON_LISIBLE;
};

/**
 * La valeur qui dit qu'un réglage est ABSENT d'un texte : celle que le lecteur rend quand aucun niveau ne le pose
 * (`lecteurDeForme.ts`). Police et taille n'en ont pas : sans elles, le lecteur rend « non lisible ».
 */
const VALEUR_D_ABSENCE: Partial<Record<CanvasTextFormatSettingKey, string>> = { bold: 'false', italic: 'false', underline: 'false', highlight: 'none', color: 'auto' };

/**
 * Vrai si cet écart à la forme dominante est une perte ÉTABLIE, à nommer au reçu.
 *
 * WHY deux exclusions, relevées dans les reçus du vrai agent (2026-10-11, assignation d'avocat) — chaque ligne de trop
 * rend le reçu `partial` et dilue celles qui comptent (l'agent n'en reposait qu'une sur trois) :
 *
 * - une valeur non lisible (`color=unreadable on "le 6 mars 2024"`) : on ne sait pas ce que le texte portait, et aucun
 *   `format_text` ne peut reposer une valeur inconnue ;
 * - un réglage ABSENT d'un texte sans lettre ni chiffre (`italic=false on "(", ")"`, `on "« ", " »"`) : des
 *   parenthèses ou des guillemets droits autour d'un texte en italique ne sont pas une mise en valeur de l'avocat.
 *
 * Un réglage absent porté par des MOTS reste nommé (l'incise romaine d'une citation en italique passerait sinon à
 * l'italique en silence), et une valeur posée sur un signe seul aussi (`[●]` surligné : un champ à remplir).
 */
const estUnePerteANommer = ({ setting, valeur, texte }: FormeNonReportee): boolean => (
  valeur !== NON_LISIBLE && (valeur !== VALEUR_D_ABSENCE[setting] || /[\p{L}\p{N}]/u.test(texte))
);

/** Comment un réglage se répartissait sur le texte du paragraphe avant l'écriture. */
interface Repartition {
  /** Vrai si tout le texte montrait la même valeur (ou si le paragraphe n'avait pas de texte). */
  uniforme: boolean;
  /** Le premier passage qui montre la valeur couvrant le plus de texte. */
  dominant: FormeDePassage | undefined;
}

function repartitionDe(passages: readonly FormeDePassage[], setting: CanvasTextFormatSettingKey): Repartition {
  const poids = new Map<string, number>();
  for (const passage of passages) poids.set(valeurMontree(passage, setting), (poids.get(valeurMontree(passage, setting)) ?? 0) + passage.texte.length);
  const [dominante] = [...poids.entries()].sort(([, a], [, b]) => b - a)[0] ?? [];
  return { uniforme: poids.size <= 1, dominant: passages.find((passage) => valeurMontree(passage, setting) === dominante) };
}

/**
 * Ce que l'éditeur doit recevoir pour qu'une plage du texte écrit montre la
 * marque `voulue`, sachant qu'il vient de lui donner la forme du PREMIER
 * passage du paragraphe (§17.2) ; `undefined` : elle la montre déjà.
 *
 * La forme directe du premier passage est EFFACÉE (`null`) quand un autre
 * passage du paragraphe montrait la valeur voulue sans rien porter à la main :
 * la cascade la donne. Sinon la valeur est écrite à la main, négation comprise.
 */
function ecritureDeLaMarque(passages: readonly FormeDePassage[], nature: NatureDeMarque, voulue: boolean): boolean | null | undefined {
  const [premier] = passages;
  // Un paragraphe sans texte reçoit la forme par défaut : seule une marque est à poser.
  if (premier === undefined) return voulue ? true : undefined;
  const base = premier[nature];
  if (base.lisible && base.valeur === voulue) return undefined;
  const sansLaMain = passages.find((passage) => !passage.aLaMain.includes(nature) && passage[nature].lisible)?.[nature];
  return premier.aLaMain.includes(nature) && sansLaMain?.lisible === true && sansLaMain.valeur === voulue ? null : voulue;
}

/**
 * La forme de caractère du texte d'un paragraphe réécrit.
 *
 * L'éditeur donne à tout le texte écrit la forme du PREMIER passage du
 * paragraphe. Une forme qui couvrait tout le paragraphe est donc gardée sans
 * rien écrire. Pour une forme qui n'en couvrait qu'une PARTIE :
 *
 * - gras, italique, souligné — le texte porte des marques de cette nature :
 *   elles DÉCIDENT d'elle sur tout le paragraphe, marqué ou non ;
 * - sinon, pour les sept réglages : le texte prend la valeur qui couvrait le
 *   plus de texte (sans cela, une étiquette grasse ou rouge en tête de
 *   paragraphe passerait à tout le texte écrit), et ce que portait le reste est
 *   rendu dans `nonReportees` quand le texte neuf porte encore ce texte
 *   (l'agent le repose par `format_text`), dans `abandonnees` sinon.
 */
export function formeDuTexteReecrit(paragraphe: ParagrapheAReecrire): FormeDuTexte {
  const partielles = paragraphe.avant.horsReglages.filter(({ partout }) => !partout);
  const horsReglages = partielles.map(({ element, texte, enTete }): FormeHorsReglagesNonReportee => (
    { element, texte, sort: enTete ? 'etendue' : porteurDansLeTexteNeuf(texte, paragraphe.texte) === undefined ? 'abandonnee' : 'perdue' }
  ));
  return { ...formeDesSeptReglages(paragraphe), ...(horsReglages.length === 0 ? {} : { horsReglages }) };
}

/** {@link formeDuTexteReecrit}, pour les sept réglages des verbes de forme. */
function formeDesSeptReglages({ avant, texte, marques }: ParagrapheAReecrire): FormeDuTexte {
  const { passages } = avant;
  const [premier] = passages;
  /** Par marque décidée : la valeur voulue à une position du texte. */
  const voulues = new Map<NatureDeMarque, (position: number) => boolean>();
  /** Les réglages sans marque à rendre au texte entier : la demande, et ce que l'éditeur reçoit pour elle. */
  const rendus: Array<{ setting: CanvasTextFormatSettingKey; demande: CanvasFormatSettingValue; inline: Record<string, boolean | string | number | null> }> = [];
  /** Tout ce qu'une partie du texte montrait d'autre que la forme dominante, avant le tri des pertes établies. */
  const ecarts: FormeNonReportee[] = [];
  for (const setting of CANVAS_TEXT_FORMAT_SETTING_KEYS) {
    const plages = estUneMarque(setting) ? marques[setting] : [];
    if (plages.length > 0 && estUneMarque(setting)) {
      voulues.set(setting, (position) => plages.some(({ debut, fin }) => debut <= position && position < fin));
      continue;
    }
    const { uniforme, dominant } = repartitionDe(passages, setting);
    if (uniforme || dominant === undefined || premier === undefined) continue;
    const dominante = valeurMontree(dominant, setting);
    passages.forEach((passage, rang) => {
      const valeur = valeurMontree(passage, setting);
      if (valeur === dominante) return;
      // Deux passages voisins qui portaient la même valeur n'en font qu'un.
      const voisin = rang > 0 && valeurMontree(passages[rang - 1]!, setting) === valeur ? ecarts.at(-1) : undefined;
      if (voisin !== undefined) voisin.texte += passage.texte;
      else ecarts.push({ setting, valeur, texte: passage.texte });
    });
    const lu = dominant[setting];
    if (!lu.lisible) continue;
    if (estUneMarque(setting)) voulues.set(setting, () => lu.valeur === true);
    else if (valeurMontree(premier, setting) !== dominante) {
      // La valeur dominante posée à la main est écrite ; donnée par un style ou par défaut, elle revient quand la forme directe du premier passage est retirée.
      const demande = normaliserLaDemande(setting, dominant.aLaMain.includes(setting) ? lu.valeur : CANVAS_FORMAT_REMOVE);
      const ecriture = ecritureDuReglage(setting, demande, [passageLuPour(premier, setting)]);
      rendus.push({ setting, demande, inline: ecriture.sansEffet ? {} : ecriture.inline });
    }
  }
  // Après la réunion des voisins : « 20 », « », « décembre » soulignés font UN texte, jugé entier.
  const nonReportees: FormeNonReportee[] = [];
  const abandonnees: FormeNonReportee[] = [];
  for (const ecart of ecarts.filter(estUnePerteANommer)) {
    const encoreLa = porteurDansLeTexteNeuf(ecart.texte, texte);
    if (encoreLa === undefined) abandonnees.push(ecart);
    else nonReportees.push({ ...ecart, texte: encoreLa });
  }
  if ((voulues.size === 0 && rendus.length === 0) || texte.length === 0) return { ecritures: [], attendus: [], nonReportees, abandonnees };

  const bornes = [...new Set([0, texte.length, ...NATURES_DE_MARQUE.flatMap((nature) => marques[nature].flatMap(({ debut, fin }) => [debut, fin]))])].sort((a, b) => a - b);
  const forme: FormeDuTexte = { ecritures: [], attendus: [], nonReportees, abandonnees };
  for (let rang = 0; rang + 1 < bornes.length; rang += 1) {
    const plage = { debut: bornes[rang]!, fin: bornes[rang + 1]! };
    const demandes: ReglagesDeTexte = {};
    const inline: Record<string, boolean | string | number | null> = {};
    for (const [nature, voulue] of voulues) {
      demandes[nature] = voulue(plage.debut);
      const ecriture = ecritureDeLaMarque(passages, nature, demandes[nature] === true);
      if (ecriture !== undefined) inline[nature] = ecriture;
    }
    for (const rendu of rendus) {
      demandes[rendu.setting] = rendu.demande;
      Object.assign(inline, rendu.inline);
    }
    forme.attendus.push({ plage, demandes });
    if (Object.keys(inline).length === 0) continue;
    // Deux plages voisines qui reçoivent la même écriture n'en font qu'une : une étape de moins.
    const precedente = forme.ecritures.at(-1);
    if (precedente !== undefined && precedente.plage.fin === plage.debut && JSON.stringify(precedente.inline) === JSON.stringify(inline)) precedente.plage.fin = plage.fin;
    else forme.ecritures.push({ plage: { ...plage }, inline });
  }
  return forme;
}

/** Vrai si le fichier porte déjà, dans ce paragraphe, le texte demandé : il n'y a pas de texte à écrire. */
export const texteDejaEnPlace = ({ avant, texte }: ParagrapheAReecrire): boolean => texteDuParagraphe(avant) === texte;

/**
 * La forme à poser sur un paragraphe dont le texte est DÉJÀ celui demandé.
 *
 * Rien n'y est réécrit : chaque passage garde donc sa forme, et non celle du
 * premier. Seules les marques du texte demandé comptent, comparées à ce que
 * chaque passage montre : une marque que le texte montre déjà n'écrit rien —
 * `ecritures` vide, le paragraphe est INCHANGÉ —, une marque nouvelle est posée
 * sur son passage. Taille, couleur, surlignage et police ne sont ni touchés ni
 * nommés : rien n'en est perdu.
 */
export function formeDuTexteEnPlace({ avant, texte, marques }: ParagrapheAReecrire): FormeDuTexte {
  const forme: FormeDuTexte = { ecritures: [], attendus: [], nonReportees: [], abandonnees: [] };
  const decidees = NATURES_DE_MARQUE.filter((nature) => marques[nature].length > 0);
  if (decidees.length === 0 || texte.length === 0) return forme;
  /** Chaque passage du fichier, avec sa position dans le texte. */
  const situes = avant.passages.reduce<Array<{ debut: number; fin: number; passage: FormeDePassage }>>((faits, passage) => {
    const debut = faits.at(-1)?.fin ?? 0;
    return passage.texte.length === 0 ? faits : [...faits, { debut, fin: debut + passage.texte.length, passage }];
  }, []);
  const bornes = [...new Set([
    0,
    texte.length,
    ...situes.map(({ debut }) => debut),
    ...decidees.flatMap((nature) => marques[nature].flatMap(({ debut, fin }) => [debut, fin])),
  ])].sort((a, b) => a - b);
  for (let rang = 0; rang + 1 < bornes.length; rang += 1) {
    const plage = { debut: bornes[rang]!, fin: bornes[rang + 1]! };
    const lu = situes.find(({ debut, fin }) => debut <= plage.debut && plage.debut < fin)?.passage;
    const demandes: ReglagesDeTexte = {};
    const inline: Record<string, boolean | string | number | null> = {};
    for (const nature of decidees) {
      const voulue = marques[nature].some(({ debut, fin }) => debut <= plage.debut && plage.debut < fin);
      demandes[nature] = voulue;
      const montre = lu?.[nature];
      if (!(montre?.lisible === true && montre.valeur === voulue)) inline[nature] = voulue;
    }
    forme.attendus.push({ plage, demandes });
    if (Object.keys(inline).length === 0) continue;
    const precedente = forme.ecritures.at(-1);
    if (precedente !== undefined && precedente.plage.fin === plage.debut && JSON.stringify(precedente.inline) === JSON.stringify(inline)) precedente.plage.fin = plage.fin;
    else forme.ecritures.push({ plage: { ...plage }, inline });
  }
  return forme;
}

/**
 * Les étapes de forme d'un paragraphe écrit, chacune adressée par sa POSITION dans le texte écrit — celle qui couvre
 * tout le texte comprise.
 *
 * WHY jamais par bloc, même pour tout le texte : la preuve commune du socle (`prouverLePlan`, `planDeFormatText.ts`)
 * ne prouve que les étapes par position — l'aperçu de l'éditeur doit situer l'étape, d'UNE plage, dans ce paragraphe,
 * à cette position, sur ce texte. Une étape de bloc s'écrit sur la foi du seul identifiant ; or la forme calculée ici
 * ne vaut que si l'éditeur porte bien le texte qu'on vient de lui écrire. Sans cette preuve, une étiquette de couleur
 * serait écrasée dans un paragraphe dont le texte n'a pas été réécrit.
 */
export function etapesDeFormeAEcrire(paragraphe: ParagrapheAReecrire, forme: FormeDuTexte): EtapeAEcrire[] {
  return forme.ecritures.map(({ plage, inline }) => ({
    paragraphe: paragraphe.avant,
    inline,
    plage: { ...plage, texte: paragraphe.texte.slice(plage.debut, plage.fin) },
  }));
}

/** Ce que la relecture dit de la forme de caractère d'un paragraphe écrit, au regard de ce qui était attendu : le jugement des verbes de forme (`jugerLeReglage`). */
export function jugerLaFormeDuTexte(relu: FormeDeParagraphe | undefined, attendus: FormeDuTexte['attendus']): CanvasFormatSettingOutcome {
  if (relu === undefined) return 'unreadable';
  let issue: CanvasFormatSettingOutcome = 'conforming';
  let position = 0;
  for (const passage of relu.passages) {
    const [debut, fin] = [position, position + passage.texte.length];
    position = fin;
    for (const { plage, demandes } of attendus) {
      if (plage.fin <= debut || fin <= plage.debut) continue;
      for (const setting of CANVAS_TEXT_FORMAT_SETTING_KEYS) {
        const demande = demandes[setting];
        if (demande === undefined) continue;
        const verdict = jugerLeReglage(setting, demande, passageLuPour(passage, setting)).issue;
        if (verdict === 'different') issue = 'different';
        else if (verdict === 'non_lisible' && issue !== 'different') issue = 'unreadable';
      }
    }
  }
  return issue;
}

/** Longueur à laquelle un texte porteur est coupé dans une ligne : assez pour le retrouver dans le paragraphe. */
const TEXTE_PORTEUR_MAX = 80;
/** Textes porteurs cités pour UN réglage d'une ligne ; au-delà ils sont comptés — le réglage, lui, est toujours nommé. */
const TEXTES_PAR_REGLAGE_MAX = 6;
/** Comment une ligne nomme un élément de forme hors des sept réglages. */
const nomHorsReglages = (element: string): string => (element.startsWith('rStyle:') ? `character style ${element.slice('rStyle:'.length)}` : element);

/**
 * La ligne d'un paragraphe dans `rewrite.formattingNotCarried` ou `rewrite.formattingDropped` : son identifiant, puis
 * les formes données — `réglage=valeur on "texte", "texte"` pour les sept réglages, réunis par valeur ; pour la forme
 * hors de ces réglages, `forme now on ALL the text (was only on "texte")`, `forme lost (was on "texte")`, ou
 * `forme on "texte"` quand elle est partie avec son texte. Seul le texte porteur est borné.
 */
export function ligneDesFormes(nodeId: string, reglages: readonly FormeNonReportee[], horsReglages: readonly FormeHorsReglagesNonReportee[]): string {
  const borne = (texte: string): string => `"${texte.length > TEXTE_PORTEUR_MAX ? `${texte.slice(0, TEXTE_PORTEUR_MAX)}…` : texte}"`;
  const parReglage = new Map<string, string[]>();
  for (const { setting, valeur, texte } of reglages) parReglage.set(`${setting}=${valeur}`, [...(parReglage.get(`${setting}=${valeur}`) ?? []), texte]);
  const perdus = [...parReglage].map(([reglage, textes]) => (
    `${reglage} on ${textes.slice(0, TEXTES_PAR_REGLAGE_MAX).map(borne).join(', ')}${textes.length > TEXTES_PAR_REGLAGE_MAX ? ` and ${textes.length - TEXTES_PAR_REGLAGE_MAX} more passage(s)` : ''}`
  ));
  const hors = horsReglages.map(({ element, texte, sort }) => (
    sort === 'etendue'
      ? `${nomHorsReglages(element)} now on ALL the text (was only on ${borne(texte)})`
      : sort === 'perdue' ? `${nomHorsReglages(element)} lost (was on ${borne(texte)})` : `${nomHorsReglages(element)} on ${borne(texte)}`
  ));
  return `${nodeId}: ${[...perdus, ...hors].join('; ')}`;
}

/** Ce que l'exécuteur a établi, et que le reçu dit. */
export interface FaitsDeLaReecriture {
  demandes: readonly ParagrapheAReecrire[];
  /**
   * Les paragraphes que l'appel écrit : ceux dont l'éditeur a appliqué l'étape de texte, et ceux dont le texte était
   * déjà en place et qui reçoivent une marque nouvelle.
   */
  appliques: ReadonlySet<string>;
  /** Ceux qui portaient déjà le texte et les marques demandés : rien n'y est écrit. */
  inchanges: ReadonlySet<string>;
  /**
   * Ceux qui ne sont pas réécrits, chacun avec sa cause : refusés avant toute écriture pour ce qu'ils portent
   * ({@link empechementDeReecriture}), ou refusés par l'éditeur. Les autres sont écrits.
   */
  sautes: ReadonlyArray<{ nodeId: string; raison: string }>;
  /** Vrai si chaque étape de texte a été soumise à l'éditeur : une interruption, s'il y en a une, est venue pendant la forme. */
  texteSoumisEnEntier: boolean;
  /** La forme de caractère attendue de chaque paragraphe écrit, par identifiant ; absent : rien n'était à poser ni à garder. */
  formes: ReadonlyMap<string, FormeDuTexte>;
  /** Le régime de suivi du document à l'entrée de l'action. */
  regimeSuivi: boolean;
  /** Par paragraphe, la révision en attente que sa réécriture reprend ({@link remplacementEntierEnAttente}), lue avant d'écrire. */
  revisionsReprises: ReadonlyMap<string, CanvasSnapshotRevision>;
  /** La liste complète des révisions relue après l'écriture ; absente : non relue, aucune reprise n'est prouvée. */
  revisionsApres: readonly CanvasSnapshotRevision[] | undefined;
  cloture: ClotureDeLEcriture;
}

/** Ce qui, d'un paragraphe, doit être identique avant et après une réécriture de son texte. */
const formeDeParagraphe = (lu: ParagrapheVise): string => JSON.stringify([lu.styleId, lu.numerotation, lu.paragraphe, lu.aLaMain, lu.traitsDeBordure]);

/**
 * Le reçu de `rewrite_block`, décidé sur le fichier relu après l'écriture.
 *
 * - `ok` : chaque paragraphe demandé porte, relu, le texte demandé, sa forme de paragraphe d'avant, et la forme de
 *   caractère de chaque passage de son ancien texte qu'il porte encore. Une forme partie avec un passage que le texte
 *   neuf ne porte plus est citée pour information (`rewrite.formattingDropped`), sans erreur.
 * - `partial` : une partie est écrite ; chaque erreur nomme le champ de `rewrite` qui dit laquelle. Rejouer n'y changerait rien.
 *   Un passage gardé sans la forme que lui seul portait en est une (décision du porteur, 2026-10-11) : nommée dans le
 *   seul effet d'un reçu `ok`, l'agent ne la reposait pas et ne le disait pas à l'avocat (8 paragraphes sur une
 *   lettre, 10 sur une assignation, vrai agent).
 * - `aborted` : l'éditeur n'a rien écrit.
 */
export function recuDeReecriture({ demandes, appliques, inchanges, sautes, texteSoumisEnEntier, formes, regimeSuivi, revisionsReprises, revisionsApres, cloture }: FaitsDeLaReecriture): CanvasActionReceipt {
  const { relecture, interruption, ecritures } = cloture;
  const commun = { intent: 'rewrite_block' as const, preSnapshot: cloture.avant, postSnapshot: cloture.apres, resolvedTargets: [] };
  const pageDite = cloture.pageNonRepeinte === true ? ` ${PAGE_NON_REPEINTE}` : '';
  const inchangesDits = inchanges.size === 0
    ? ''
    : ` ${inchanges.size} paragraph(s) already carried the requested text and marks: nothing was written there, their formatting is untouched (rewrite.unchanged).`;

  if (appliques.size === 0 && inchanges.size === demandes.length) {
    // Rien à écrire n'est pas un échec : le document porte déjà ce qui est demandé.
    return {
      ...commun,
      status: 'ok',
      reviewability: 'none',
      effects: [{
        kind: 'rewrite_block',
        reviewability: 'none',
        description: `Nothing was written: each of the ${demandes.length} paragraph(s) already carries the requested text and marks (rewrite.unchanged). Their formatting is untouched; there is nothing to undo.`,
      }],
      verification: [{ name: 'texts_reread', passed: true, message: 'Read from the exported document file, each paragraph already carries exactly the requested text.' }],
      verificationPassed: true,
      errors: [],
      rewrite: { requested: demandes.length, written: 0, unchanged: inchanges.size, writes: 0 },
    };
  }

  if (appliques.size === 0) {
    const cause = interruption !== undefined
      ? `the editor refused the write (${interruption.detail})`
      : `none of the ${sautes.length} paragraph(s) to write could be rewritten — ${sautes.map(({ nodeId, raison }) => `${nodeId}: ${raison}`).join(' ; ')}`;
    return {
      ...commun,
      status: 'aborted',
      reviewability: 'none',
      effects: [],
      verification: [],
      verificationPassed: false,
      errors: [{ code: interruption?.code ?? 'REWRITE_BLOCKS_REFUSED', message: `${INTENTION} wrote nothing: ${cause}. The document is unchanged.${inchangesDits}${pageDite}` }],
      recovery: { action: 'reinspect', reason: 'Nothing was written. Read the targeted paragraphs again before another write.' },
    };
  }

  const ecrits = demandes.filter(({ nodeId }) => appliques.has(nodeId));
  const relus = 'acte' in relecture ? new Map(relecture.acte.paragraphes.flatMap((lu) => (lu.nodeId === null ? [] : [[lu.nodeId, lu] as const]))) : undefined;
  const textesDifferents = relus === undefined ? [] : ecrits.filter(({ nodeId, texte }) => texteDuParagraphe(relus.get(nodeId) ?? { passages: [] }) !== texte).map(({ nodeId }) => nodeId);
  const formesChangees = relus === undefined
    ? []
    : ecrits.filter(({ nodeId, avant }) => relus.has(nodeId) && formeDeParagraphe(relus.get(nodeId)!) !== formeDeParagraphe(avant)).map(({ nodeId }) => nodeId);
  const written = ecrits.length - textesDifferents.length;
  // Un paragraphe dont la réécriture reprend la révision en attente n'en crée pas : elle est toujours là après, le
  // texte neuf dedans (mesure du 2026-10-13, `remplacementEntierEnAttente`), prouvé sur la révision relue.
  const reecrits = ecrits.filter((paragraphe) => !texteDejaEnPlace(paragraphe));
  const reprises = !regimeSuivi || revisionsApres === undefined ? [] : reecrits.flatMap(({ nodeId, texte }) => {
    const avant = revisionsReprises.get(nodeId);
    return avant === undefined || !repriseProuvee(avant, revisionsApres, nodeId, texte) ? [] : [avant.id];
  });
  // WHY par paragraphe : une identité neuve dans le lot ne dit rien d'un AUTRE paragraphe. Jugé sur tout le lot, un
  // paragraphe ordinaire qui créait sa révision faisait dire suivi un voisin en reprise non prouvée — reçu `ok`, sans
  // `TRACKING_NOT_APPLIED`, et un texte écrit hors de toute révision annoncé relisible (revue de la PR #2257). Chaque
  // paragraphe réécrit est couvert par SA révision : la reprise prouvée de celle qu'il portait, ou une révision neuve
  // ancrée sur lui (`blockId`, l'ancrage que la reprise elle-même suppose). Liste relue non prouvée complète : la
  // reprise reste non prouvée, et pour un paragraphe ordinaire l'apparition d'une identité neuve reste le seul signal.
  const nouvelles = new Set(cloture.revisions.nouvelles);
  const reprisesProuvees = new Set(reprises);
  const sansRevisionProuvee = reecrits.filter(({ nodeId }) => {
    const reprise = revisionsReprises.get(nodeId);
    if (reprise !== undefined) return !reprisesProuvees.has(reprise.id);
    return revisionsApres === undefined
      ? nouvelles.size === 0
      : !revisionsApres.some((revision) => nouvelles.has(revision.id) && revision.blockId === nodeId);
  }).map(({ nodeId }) => nodeId);
  // Sans paragraphe réécrit (marques posées sur un texte déjà en place), la règle reste celle des identités neuves.
  const suivi = reecrits.length === 0 ? nouvelles.size > 0 : regimeSuivi && sansRevisionProuvee.length === 0;
  const suiviEnPartie = regimeSuivi && !suivi && sansRevisionProuvee.length < reecrits.length;
  // La forme de caractère n'est jugée que là où le texte relu est celui écrit : ailleurs, ses plages ne désignent plus rien.
  const formesJugees = ecrits.flatMap(({ nodeId }) => {
    const forme = formes.get(nodeId);
    return forme === undefined || forme.attendus.length === 0 || textesDifferents.includes(nodeId) ? [] : [{ nodeId, issue: relus === undefined ? 'unreadable' as const : jugerLaFormeDuTexte(relus.get(nodeId), forme.attendus) }];
  });
  const formesNonConformes = formesJugees.filter(({ issue }) => issue !== 'conforming');
  const issueDeLaForme: CanvasFormatSettingOutcome = formesNonConformes.some(({ issue }) => issue === 'different') ? 'different' : formesNonConformes.length > 0 ? 'unreadable' : 'conforming';

  const errors: CanvasActionReceipt['errors'] = [];
  if ('nonRelu' in relecture) {
    errors.push({
      code: 'REWRITE_NOT_REREAD',
      message: `${INTENTION}: the editor applied the text of ${ecrits.length} paragraph(s), but the document file could not be re-read after the write (${relecture.nonRelu}), `
        + 'so rewrite.written counts what the editor accepted, not what was re-read. Do not resend the action; read the paragraphs (canvas_inspect) to check them.',
    });
  }
  if (textesDifferents.length > 0) {
    errors.push({
      code: 'REWRITE_TEXT_DIFFERENT',
      message: `${INTENTION}: re-read from the document file, ${textesDifferents.length} paragraph(s) do not carry the requested text (${citer(textesDifferents)}) — `
        + 'the lawyer typed during the call, or the editor wrote something else. rewrite.written counts the others. Read these paragraphs before another write.',
    });
  }
  if (formesChangees.length > 0) {
    errors.push({
      code: 'REWRITE_PARAGRAPH_FORM_CHANGED',
      message: `${INTENTION}: the paragraph formatting of ${formesChangees.length} rewritten paragraph(s) is no longer the one read before the write (${citer(formesChangees)}). `
        + 'Read their form (canvas_inspect formatOf) and tell the lawyer what changed.',
    });
  }
  if (relus !== undefined && formesNonConformes.length > 0) {
    errors.push({
      code: issueDeLaForme === 'different' ? 'REWRITE_TEXT_FORM_DIFFERENT' : 'REWRITE_TEXT_FORM_UNREADABLE',
      message: `${INTENTION}: the texts are written, but in ${formesNonConformes.length} paragraph(s) the character formatting re-read from the document file `
        + `${issueDeLaForme === 'different' ? 'is not the one this rewrite set' : 'could not be read'}: rewrite.textFormatting.differentIn names them. `
        + 'Do not resend the rewrite. Read their form (canvas_inspect formatOf), then set it with format_text on the passages concerned.',
    });
  }
  if (sautes.length > 0) {
    errors.push({
      code: CANVAS_REWRITE_BLOCKS_SKIPPED,
      message: `${INTENTION}: ${sautes.length} of the ${demandes.length} paragraph(s) were not rewritten; the others are written. rewrite.skipped gives, for each, the reason `
        + '(what it carries that a whole-paragraph rewrite would delete, or the editor\'s refusal) and the way to change it instead'
        + '. '
        + 'Do not resend the whole list. Change these paragraphs as their reason says, on the same nodeId, or tell the lawyer.',
    });
  }
  if (interruption !== undefined) {
    // Toutes les étapes de texte soumises : l'interruption est venue pendant la forme, le texte de chaque paragraphe est écrit.
    const pendantLaForme = texteSoumisEnEntier;
    errors.push({
      code: interruption.code ?? 'REWRITE_INTERRUPTED',
      message: `${INTENTION} was interrupted ${pendantLaForme ? 'while setting the character formatting of the written text' : `after writing ${ecrits.length} of the ${demandes.length} paragraph(s)`}: `
        + `${interruption.detail}. What is written stays written. Read the document again before another write.`,
    });
  }
  // Sans paragraphe réécrit, seule de la forme a été écrite (marques posées sur un texte déjà en place) : une écriture
  // de forme est directe par conception (`no_tracked_change_created`, `recuDEcritureDeForme.ts`), il n'y a aucun texte à
  // relire en révision. L'erreur rendait `partial`, portée fermée, un simple gras sous le régime par défaut, et disait
  // à tort « the requested text IS in the document… » ; le reçu dit `direct_untracked`, comme `format_text`.
  if (regimeSuivi && reecrits.length > 0 && ecritures > 0 && !suivi) {
    errors.push({
      code: 'TRACKING_NOT_APPLIED',
      // Le message ne dit « aucune révision » que s'il n'en est apparu aucune : sinon il nomme les paragraphes sans la leur.
      message: suiviEnPartie || (nouvelles.size > 0 && reecrits.length > 0)
        ? `${INTENTION}: the requested text IS in the document, but ${sansRevisionProuvee.length} of the ${reecrits.length} rewritten paragraph(s) carry no tracked change of their own (${citer(sansRevisionProuvee)}): `
          + `the lawyer cannot review ${sansRevisionProuvee.length === 1 ? 'it' : 'them'} as a change${suiviEnPartie ? '; the other rewritten paragraphs are tracked changes' : ''}. `
          + 'Do not resend the action. Say which paragraphs were written without being reviewable.'
        : `${INTENTION}: the requested text IS in the document, but the editor produced no tracked change although tracking is on. `
          + 'Do not tell the lawyer to accept or reject these changes: there are none to review. Do not resend the action. Say what was written, and that it is not reviewable.',
    });
  }
  if (cloture.pageNonRepeinte === true) errors.push({ code: 'FORMAT_PAGE_NOT_REPAINTED', message: `${INTENTION}: ${PAGE_NON_REPEINTE}` });

  // Comme la forme jugée : seuls les paragraphes dont le texte relu est celui écrit. Ailleurs, l'erreur de texte dit de relire.
  const reportables = ecrits.filter(({ nodeId }) => !textesDifferents.includes(nodeId));
  /** Une ligne par paragraphe qui porte au moins une forme de la sorte demandée : non reportée (le reçu est `partial`) ou abandonnée (citée pour information). */
  const lignesDe = (abandonnee: boolean): string[] => reportables.flatMap(({ nodeId }) => {
    const forme = formes.get(nodeId);
    if (forme === undefined) return [];
    const hors = (forme.horsReglages ?? []).filter(({ sort }) => (sort === 'abandonnee') === abandonnee);
    const reglages = abandonnee ? forme.abandonnees : forme.nonReportees;
    return reglages.length + hors.length === 0 ? [] : [ligneDesFormes(nodeId, reglages, hors)];
  });
  const nonReportees = lignesDe(false);
  const abandonnees = lignesDe(true);
  const horsDesSept = reportables.some(({ nodeId }) => (formes.get(nodeId)?.horsReglages ?? []).some(({ sort }) => sort !== 'abandonnee'));
  // ⚠️ Accordé au courtier (`estReecriturePartielleProuvee`, backend) : il garde la portée de lecture quand le reçu ne
  // porte que des paragraphes sautés et des formes non reportées. Toute autre erreur la ferme.
  const porteeGardee = errors.every(({ code }) => code === CANVAS_REWRITE_BLOCKS_SKIPPED);
  if (nonReportees.length > 0) {
    errors.push({
      code: CANVAS_REWRITE_FORMATTING_NOT_CARRIED,
      message: `${INTENTION}: ${nonReportees.length} paragraph(s) still contain a passage of their old text, without the formatting that passage carried. `
        + 'Their text IS written: do not call rewrite_block again for them. '
        + 'rewrite.formattingNotCarried gives, per paragraph, each setting=value and the passage as the new text has it. '
        + (porteeGardee ? 'Now, without canvas_inspect' : 'After the canvas_inspect this receipt\'s recovery asks for')
        + ', set each one again: format_text with target {kind:"text", nodeId, text} and that setting (a passage ending in "…" is cut at 80 characters, and a passage the paragraph contains more than once needs its neighbouring words: target each as the paragraph reads); '
        + 'tell the lawyer about any you leave as it is.'
        + (horsDesSept
          ? ' A line that says "now on ALL the text" or "lost" names a character formatting no formatting verb sets (small caps, strikethrough, superscript, a character style): '
            + 'the editor spread the one that opened the paragraph over the whole new text, and dropped the others — tell the lawyer, who can fix it from the toolbar.'
          : ''),
    });
  }

  const verification: VerificationCheck[] = [
    { name: 'texts_reread', passed: relus !== undefined && textesDifferents.length === 0, message: 'Re-read from the exported document file, each rewritten paragraph must carry exactly the requested text.' },
    { name: 'paragraph_form_kept', passed: relus !== undefined && formesChangees.length === 0, message: 'Each rewritten paragraph must keep the paragraph formatting it had before the write.' },
    { name: 'every_block_written', passed: sautes.length === 0 && (interruption === undefined || texteSoumisEnEntier), message: 'Every paragraph of the list that needed a write must have been written.' },
    ...(formesJugees.length === 0 ? [] : [{ name: 'text_formatting_reread', passed: issueDeLaForme === 'conforming', message: 'Re-read from the exported document file, the written text must show the bold, italic and underline it was given.' }]),
    { name: 'partial_formatting_carried', passed: nonReportees.length === 0, message: 'A passage of a paragraph\'s old text that its new text still contains must keep the formatting it carried.' },
  ];
  const reviewability: CanvasActionReceipt['reviewability'] = suivi ? 'tracked' : suiviEnPartie ? 'mixed' : ecritures > 0 ? 'direct_untracked' : 'none';
  const rewrite: CanvasRewriteReceipt = {
    requested: demandes.length,
    written,
    ...(inchanges.size === 0 ? {} : { unchanged: inchanges.size }),
    writes: ecritures,
    ...(sautes.length === 0 ? {} : { skipped: sautes.map(({ nodeId, raison }) => ({ nodeId, reason: raison.slice(0, 600) })) }),
    ...(formesJugees.length === 0
      ? {}
      : {
          textFormatting: {
            paragraphs: formesJugees.length,
            outcome: issueDeLaForme,
            ...(formesNonConformes.length === 0 ? {} : { differentIn: formesNonConformes.map(({ nodeId }) => nodeId) }),
          },
        }),
  };
  // TOUS les paragraphes concernés de l'appel sont nommés : l'agent ne peut reposer que ce qu'il lit ici.
  if (nonReportees.length > 0) rewrite.formattingNotCarried = nonReportees;
  if (abandonnees.length > 0) rewrite.formattingDropped = abandonnees;
  // L'effet dit le fait ; la suite à donner est dans l'erreur `CANVAS_REWRITE_FORMATTING_NOT_CARRIED`, pas ici deux fois.
  const nonReporteesDites = nonReportees.length === 0
    ? ''
    : ` ${nonReportees.length} of them still contain a passage of their old text without the formatting it carried: rewrite.formattingNotCarried names all ${nonReportees.length}, one paragraph per line, `
      + 'with every setting=value and the passage (each passage cut at 80 characters).';
  // Un champ du reçu sans phrase qui le nomme n'a aucune notice côté modèle. Celle-ci dit ce que le reçu sait (le
  // passage n'est plus là tel quel, son remplaçant éventuel n'a pas la forme) sans nommer de verbe : un reçu ok qui
  // pousserait à `format_text` rejouerait la boucle de production sur un passage retiré exprès.
  const abandonneesDites = abandonnees.length === 0
    ? ''
    : ` ${abandonnees.length} of them no longer contain, as it was written, a passage that carried its own formatting: rewrite.formattingDropped lists it. `
      + 'A passage you wrote in its place (another amount, another wording) does not have that formatting.';
  // Un texte déjà en place n'est pas réécrit : son ancre de commentaire n'a pas bougé.
  const ancresDeplacees = ecrits.filter((paragraphe) => !texteDejaEnPlace(paragraphe) && paragraphe.avant.horsTexte.includes('comment_anchor')).map(({ nodeId }) => nodeId);
  if (ancresDeplacees.length > 0) rewrite.commentAnchorsMoved = ancresDeplacees;
  const ancresDites = ancresDeplacees.length === 0
    ? ''
    : ` ${ancresDeplacees.length} of them carried a comment: the comment is kept, but its anchor now sits at the end of the paragraph instead of around the commented text (rewrite.commentAnchorsMoved) — tell the lawyer.`;
  // La règle du socle, pas une seconde : sous un historique que les écritures n'expliquent pas, le compte ne dit plus quoi annuler.
  const { incertaine, nonAnnulables, dite } = annulationDeLEcriture(cloture, 'rewrite.writesNotUndoable');
  if (nonAnnulables > 0) rewrite.writesNotUndoable = nonAnnulables;
  const reprisesDites = reprises.length === 0
    ? ''
    : ` ${reprises.length} of them already carried a pending tracked change replacing their whole text: no new tracked change was created there, the new text replaced the proposed one inside that same tracked change (${reprises.slice(0, 8).join(', ')}), and rejecting it still gives back the original text.`;
  const entrees = `${ecritures} history entr${ecritures === 1 ? 'y' : 'ies'}`;
  const annulation = incertaine
    ? `${entrees}.${dite}`
    : nonAnnulables > 0
      ? `${entrees}, of which the first ${nonAnnulables} can no longer be undone (the editor keeps its last ${ecritures - nonAnnulables} writes; rewrite.writesNotUndoable): undo_changes with steps:${ecritures - nonAnnulables} undoes the others.`
      : `${entrees}: undo_changes with steps:${ecritures} undoes this call.`;
  return {
    ...commun,
    status: errors.length === 0 ? 'ok' : 'partial',
    reviewability,
    effects: [{
      kind: 'rewrite_block',
      reviewability,
      description: `Rewrote ${written} paragraph(s) in place${suivi ? ', as tracked changes' : ''}: each keeps its paragraph formatting (indents, alignment, spacing, border, style).${inchangesDits}${reprisesDites}${nonReporteesDites}${abandonneesDites}${ancresDites} ${annulation.trimEnd()}`,
    }],
    verification,
    verificationPassed: errors.length === 0,
    errors,
    rewrite,
    ...(cloture.revisions.nouvelles.length + cloture.revisions.disparues.length === 0
      ? {}
      // Une révision en attente dans un paragraphe réécrit part avec son texte : aucune disparition n'est tenue pour celle d'un autre auteur.
      : { revisionIdentities: { ...cloture.revisions, disparuesInattendues: [] } }),
    // ⚠️ Accordé au courtier (`estReecriturePartielleProuvee`, backend) : un reçu `partial` ferme la portée de lecture,
    // donc toute écriture suivante demande une lecture. Seule exception, le reçu qui ne porte que des paragraphes
    // sautés et des formes non reportées : la portée reste, la suite est dans chaque message, et aucune consigne de
    // reprise ne dit de relire.
    ...(errors.every(({ code }) => code === CANVAS_REWRITE_BLOCKS_SKIPPED || code === CANVAS_REWRITE_FORMATTING_NOT_CARRIED)
      ? {}
      : { recovery: { action: 'reinspect' as const, reason: 'Part of the rewrite is written and stays written: do not resend the list. This partial receipt closes the reading scope: read the document again (canvas_inspect) once, then do what each error says.' } }),
  };
}
